import { useEffect, useState, type RefObject } from "react";
import { AGENT_PERSONAS } from "@/config/agents";
import { env } from "@/config/env";
import { cdnUrl, cn } from "@/lib/utils";
import { appEvents } from "@/shared/lib/events";
import { SimliAvatar, type AvatarHandle } from "@/features/companion";

const MARCUS_PERSONA = AGENT_PERSONAS.operator;
// Pre-cropped square headshot, framed to match the live Simli render (hairline near the top,
// neck/shoulders visible below) so the static→live swap is seamless. Square asset in a square
// frame, so object-cover shows it 1:1; raise scale above 1 only to zoom the head in further.
const MARCUS_ONBOARDING_STATIC_AVATAR_SRC = cdnUrl("/Marcus-onboarding.jpg");
const MARCUS_ONBOARDING_AVATAR_MEDIA_CLASS_NAME = "scale-100";
const MARCUS_ONBOARDING_AVATAR_OBJECT_POSITION = "center";

export interface MarcusOnboardingIntroAvatarProps {
  avatarRef: RefObject<AvatarHandle | null>;
  compact: boolean;
  liveConnected: boolean;
  liveSpeaking: boolean;
  voiceIntroActive: boolean;
  onConnected: () => void;
  onDisconnected: () => void;
  onStartSpeaking: () => void;
  onStopSpeaking: () => void;
}

export function MarcusOnboardingIntroAvatar({
  avatarRef,
  compact,
  liveConnected,
  liveSpeaking,
  voiceIntroActive,
  onConnected,
  onDisconnected,
  onStartSpeaking,
  onStopSpeaking,
}: MarcusOnboardingIntroAvatarProps) {
  const canUseLiveAvatar = Boolean(
    env.nodeEnv !== "test" &&
      env.simliApiKey &&
      MARCUS_PERSONA.simliFaceId &&
      MARCUS_PERSONA.voiceId,
  );
  const [liveAvatarSeen, setLiveAvatarSeen] = useState(false);

  useEffect(() => {
    if (!compact) return;
    appEvents.publish({ type: "marcus-onboarding:compact-avatar-mounted" });
    if (!canUseLiveAvatar) {
      appEvents.publish({ type: "marcus-onboarding:compact-avatar-unavailable" });
    }
  }, [canUseLiveAvatar, compact]);

  useEffect(() => {
    if (!canUseLiveAvatar || !voiceIntroActive) {
      setLiveAvatarSeen(false);
      return;
    }
    if (liveConnected) setLiveAvatarSeen(true);
  }, [canUseLiveAvatar, liveConnected, voiceIntroActive]);

  useEffect(() => {
    if (compact && liveConnected) {
      appEvents.publish({ type: "marcus-onboarding:compact-avatar-ready" });
    }
  }, [compact, liveConnected]);

  const showLiveAvatar = liveConnected || (voiceIntroActive && liveAvatarSeen);

  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden rounded-full border bg-surface-high shadow-[0_18px_44px_rgba(28,27,27,0.16)]",
        compact
          ? "h-[88px] w-[88px] border-white/70"
          : "h-[76px] w-[76px] border-outline-variant/70",
      )}
    >
      <div data-marcus-onboarding-avatar-fallback className="absolute inset-0">
        <img
          src={MARCUS_ONBOARDING_STATIC_AVATAR_SRC}
          alt=""
          loading="eager"
          decoding="async"
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            MARCUS_ONBOARDING_AVATAR_MEDIA_CLASS_NAME,
          )}
          style={{ objectPosition: MARCUS_ONBOARDING_AVATAR_OBJECT_POSITION }}
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle,transparent_56%,rgba(0,0,0,0.2)_100%)]"
        />
      </div>
      {canUseLiveAvatar ? (
        <div
          data-marcus-onboarding-live-avatar
          aria-hidden={!showLiveAvatar}
          className={cn(
            "absolute inset-0 transition-opacity duration-300",
            showLiveAvatar ? "opacity-100" : "opacity-0",
          )}
        >
          <SimliAvatar
            ref={avatarRef}
            faceId={MARCUS_PERSONA.simliFaceId!}
            voiceId={MARCUS_PERSONA.voiceId}
            isMuted={false}
            volume={0.9}
            hideGestureGate
            onConnected={onConnected}
            onDisconnected={onDisconnected}
            onStartSpeaking={onStartSpeaking}
            onStopSpeaking={onStopSpeaking}
          />
        </div>
      ) : null}
      <span
        aria-hidden="true"
        className={cn(
          "absolute bottom-1 right-1 h-3 w-3 rounded-full border-2 border-surface-lowest",
          liveSpeaking
            ? "animate-pulse bg-primary"
            : liveConnected
              ? "bg-emerald-400"
              : voiceIntroActive
                ? "bg-surface-highest"
                : "bg-surface-high",
        )}
      />
    </div>
  );
}
